/**
 * Public CMS Page — renders block-based pages at /p/:slug
 *
 * Pulls page data + SEO metadata from /api/cms/pages/:slug.
 * Returns 404 for unpublished pages.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { resolveImageUrl } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BlockRenderer from "@/components/BlockRenderer";

export default function CmsPageView() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!slug) return;
        setLoading(true);
        setNotFound(false);
        api.get(`/cms/pages/${slug}`)
            .then((r) => setPage(r.data))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [slug]);

    // Apply SEO meta tags imperatively (no react-helmet dep)
    useEffect(() => {
        if (!page) return;
        const seo = page.seo || {};
        const prevTitle = document.title;
        document.title = seo.title || page.title;
        const created = [];
        const setMeta = (sel, attrs) => {
            let el = document.querySelector(sel);
            if (!el) {
                el = document.createElement(sel.startsWith("link") ? "link" : "meta");
                Object.entries(attrs.create || {}).forEach(([k, v]) => el.setAttribute(k, v));
                document.head.appendChild(el);
                created.push(el);
            }
            Object.entries(attrs.set || {}).forEach(([k, v]) => el.setAttribute(k, v));
        };
        if (seo.description) setMeta('meta[name="description"]', { create: { name: "description" }, set: { content: seo.description } });
        if (seo.robots) setMeta('meta[name="robots"]', { create: { name: "robots" }, set: { content: seo.robots } });
        if (seo.canonical) setMeta('link[rel="canonical"]', { create: { rel: "canonical" }, set: { href: seo.canonical } });
        if (seo.og_image_url) setMeta('meta[property="og:image"]', { create: { property: "og:image" }, set: { content: resolveImageUrl(seo.og_image_url) } });
        return () => {
            document.title = prevTitle;
            created.forEach((el) => el.remove());
        };
    }, [page]);

    if (loading) {
        return (
            <div className="bg-slate-50 dark:bg-slate-950 min-h-screen">
                <Navbar />
                <div className="container mx-auto px-4 md:px-6 max-w-5xl py-20 space-y-4">
                    <div className="h-12 rounded-xl bg-slate-200 dark:bg-slate-800 animate-pulse w-2/3" />
                    <div className="h-64 rounded-2xl bg-slate-200 dark:bg-slate-800 animate-pulse" />
                </div>
                <Footer />
            </div>
        );
    }

    if (notFound || !page) {
        return (
            <div className="bg-slate-50 dark:bg-slate-950 min-h-screen">
                <Navbar />
                <div className="container mx-auto px-4 md:px-6 max-w-3xl py-32 text-center" data-testid="cms-page-notfound">
                    <div className="text-6xl mb-4">404</div>
                    <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-white mb-2">Sayfa bulunamadı</h1>
                    <p className="text-slate-500 mb-6">Aradığınız sayfa kaldırılmış veya hiç yayınlanmamış olabilir.</p>
                    <button onClick={() => navigate("/")} className="px-5 py-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold">Anasayfaya Dön</button>
                </div>
                <Footer />
            </div>
        );
    }

    const seo = page.seo || {};
    return (
        <div className="bg-slate-50 dark:bg-slate-950 min-h-screen" data-testid="cms-page">
            <Navbar />
            <BlockRenderer blocks={page.blocks || []} />
            <Footer />
        </div>
    );
}
